import { AnalyticsEventName, AnalyticsPageType } from '@shopify/hydrogen-react';
import { shopifyAnalyticsConfig, shopifyConfig } from '../data/shopify';
import { basePayload, resolveShopGid, sendToShopify, shopifyAnalyticsEnabled } from './shopifyAnalytics';

/**
 * Mirrors funnel add-to-cart actions into a real Shopify cart (Storefront API, Headless channel)
 * and reports Shopify's own ADD_TO_CART analytics event, so the store sees an active cart.
 * At checkout, a cart with the exact final order is created and the customer is sent to ITS
 * checkout, so Shopify sees one continuous journey: visit → cart → checkout → purchase.
 */

const CART_KEY = 'mfl_shopify_cart_id';

type CartVariant = {
  id: string;
  title: string;
  price: { amount: string };
  product: { id: string; title: string };
};

type CartResult = {
  cart: { id: string; checkoutUrl: string; lines: { nodes: { merchandise: CartVariant }[] } } | null;
  userErrors: { message: string }[];
};

const CART_FIELDS = `
  cart {
    id
    checkoutUrl
    lines(first: 50) {
      nodes {
        merchandise {
          ... on ProductVariant { id title price { amount } product { id title } }
        }
      }
    }
  }
  userErrors { message }
`;

async function storefront<T>(query: string, variables: Record<string, unknown>): Promise<T | null> {
  const { storefrontToken, storefrontApiVersion } = shopifyAnalyticsConfig;
  const res = await fetch(`https://${shopifyConfig.storeDomain}/api/${storefrontApiVersion}/graphql.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': storefrontToken.trim() },
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) return null;
  const json = (await res.json()) as { data?: T };
  return json.data ?? null;
}

const getCartId = () => {
  try {
    return sessionStorage.getItem(CART_KEY);
  } catch {
    return null;
  }
};

const setCartId = (id: string | null) => {
  try {
    if (id) sessionStorage.setItem(CART_KEY, id);
    else sessionStorage.removeItem(CART_KEY);
  } catch {
    // ignore
  }
};

const toGid = (variantId: string) => `gid://shopify/ProductVariant/${variantId}`;

async function addLine(variantGid: string, quantity: number): Promise<CartResult['cart']> {
  const lines = [{ merchandiseId: variantGid, quantity }];
  const existing = getCartId();
  if (existing) {
    const data = await storefront<{ cartLinesAdd: CartResult }>(
      `mutation add($cartId: ID!, $lines: [CartLineInput!]!) { cartLinesAdd(cartId: $cartId, lines: $lines) { ${CART_FIELDS} } }`,
      { cartId: existing, lines },
    );
    const cart = data?.cartLinesAdd.cart;
    if (cart && !data?.cartLinesAdd.userErrors.length) return cart;
    setCartId(null); // expired or invalid cart — start a new one
  }
  const data = await storefront<{ cartCreate: CartResult }>(
    `mutation create($lines: [CartLineInput!]) { cartCreate(input: { lines: $lines }) { ${CART_FIELDS} } }`,
    { lines },
  );
  const cart = data?.cartCreate.cart ?? null;
  if (cart) setCartId(cart.id);
  return cart;
}

const cartEnabled = () =>
  typeof window !== 'undefined' && shopifyAnalyticsEnabled() && shopifyAnalyticsConfig.storefrontToken.trim().length > 0;

/** Fire-and-forget: adds the system to the Shopify cart and sends Shopify's ADD_TO_CART event. */
export function shopifyAddToCart(variantId: string, quantity: number) {
  if (!cartEnabled()) return;
  const variantGid = toGid(variantId);

  Promise.all([addLine(variantGid, quantity), resolveShopGid()])
    .then(([cart, shopId]) => {
      if (!cart || !shopId) return;
      const line = cart.lines.nodes.find((n) => n.merchandise?.id === variantGid)?.merchandise;
      if (!line) return;
      return sendToShopify({
        eventName: AnalyticsEventName.ADD_TO_CART,
        payload: {
          ...basePayload(shopId, AnalyticsPageType.home),
          cartId: cart.id,
          totalValue: Number(line.price.amount) * quantity,
          products: [
            {
              productGid: line.product.id,
              variantGid: line.id,
              name: line.product.title,
              variantName: line.title,
              brand: 'MarineForceLED',
              price: line.price.amount,
              quantity,
            },
          ],
        },
      });
    })
    .catch(() => {
      // Never block or break the purchase flow.
    });
}

/**
 * Creates a Shopify cart with the exact final order (variant:qty) plus attribution as cart
 * attributes, and returns its checkout URL. Resolves to null on any error or after `timeoutMs`,
 * so the caller can fall back to the cart permalink — the customer is never stuck.
 */
export async function createCheckoutCart(
  lines: { variantId: string; qty: number }[],
  attribution: Record<string, string>,
  timeoutMs = 1500,
): Promise<string | null> {
  if (!cartEnabled() || !lines.length) return null;

  const attempt = storefront<{ cartCreate: CartResult }>(
    `mutation create($input: CartInput!) { cartCreate(input: $input) { ${CART_FIELDS} } }`,
    {
      input: {
        lines: lines.map((l) => ({ merchandiseId: toGid(l.variantId), quantity: l.qty })),
        attributes: Object.entries(attribution).map(([key, value]) => ({ key, value })),
      },
    },
  )
    .then((data) => {
      const res = data?.cartCreate;
      if (!res?.cart?.checkoutUrl || res.userErrors.length) return null;
      // Verify every line made it in; otherwise use the permalink (exact order guaranteed).
      if (res.cart.lines.nodes.length < lines.length) return null;
      setCartId(res.cart.id);
      const url = new URL(res.cart.checkoutUrl);
      for (const [k, v] of Object.entries(attribution)) url.searchParams.set(k, v);
      return url.toString();
    })
    .catch(() => null);

  const timeout = new Promise<null>((resolve) => window.setTimeout(() => resolve(null), timeoutMs));
  return Promise.race([attempt, timeout]);
}
